import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FulfillService } from '../../fulfill.service';
import { BatchSocketResponse, PanelState } from '../../utils/models';
import { ShippingManifestsListComponent } from '../fulfill-shipping-manifests/shipping-manifests-list/shipping-manifests-list.component';
import { OrderBatchCategory, OrderBatchDTO, PdqUser } from '@pdqdev/pdq-shared/dist/entities';
import { UserManagementService } from '@app/services/user-management/user-management.service';
import { LayoutService } from '@app/storybook/services/layout/layout.service';
import { OrderBatchService } from '@app/services/order-batch/order-batch.service';
import { BatchesState, BatchFilters } from '@app/sharedModule/models/paginated-batch-table.model';
import { FULFILL_BATCH_TABLE_COLUMNS } from './fulfill-batch-list-columns.config';
import { FulfillBatchListTableV2Component } from '../fulfill-batch-list-table-v2/fulfill-batch-list-table-v2.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { WebsocketSubscriberService } from '@app/core/services/websocket-subscriber/websocket.subscriber.service';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, tap } from 'rxjs/operators';
import { OrderBatchVirtualColumn } from '@pdqdev/pdq-shared/dist/interfaces';

@Component({
  selector: 'fulfill-batch-list-v2',
  templateUrl: './fulfill-batch-list-v2.component.html',
  styleUrls: ['./fulfill-batch-list-v2.component.scss'],
  standalone: false,
})
export class FulfillBatchListV2Component implements OnInit, OnDestroy {
  @ViewChild('fulfillmentTable') fulfillmentTable: FulfillBatchListTableV2Component;
  @ViewChild('deliveryTable') deliveryTable: FulfillBatchListTableV2Component;

  readonly OrderBatchCategory = OrderBatchCategory;

  activeTab: OrderBatchCategory = OrderBatchCategory.FULFILLMENT_READY;
  pdqUsersList: PdqUser[];
  columns = FULFILL_BATCH_TABLE_COLUMNS;
  showBatchHistory = signal<boolean>(false);
  fulfillmentTabLabel = computed(() =>
    this.buildTabLabel('Fulfillment ready', this._fulfillmentReadyTotalCount(), this._fulfillmentCountLoading()),
  );
  deliveryTabLabel = computed(() =>
    this.buildTabLabel('Delivery ready', this._deliveryReadyTotalCount(), this._deliveryCountLoading()),
  );

  private pageSizeChange = new Subject<number>();
  private destroyRef = inject(DestroyRef);
  private _fulfillmentReadyTotalCount = signal<number>(0);
  private _deliveryReadyTotalCount = signal<number>(0);
  private _fulfillmentCountLoading = signal<boolean>(false);
  private _deliveryCountLoading = signal<boolean>(false);
  private _fulfillmentReadyBatches = signal<BatchesState>({
    pageCache: new Map(),
    loadingPages: new Set(),
    pageSize: 5,
    filters: {},
  });
  private _deliveryReadyBatches = signal<BatchesState>({
    pageCache: new Map(),
    loadingPages: new Set(),
    pageSize: 5,
    filters: {},
  });
  private static readonly _BATCH_DTO_VIRTUAL_COLUMNS: OrderBatchVirtualColumn[] = [
    'fulfilledOrdersCount',
    'hasAddedOrders',
    'hasWarnings',
  ];

  constructor(
    public fulfillService: FulfillService,
    public layoutService: LayoutService,
    private dialog: MatDialog,
    private umService: UserManagementService,
    private orderBatchService: OrderBatchService,
    private toastService: ToastService,
    private wssService: WebsocketSubscriberService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnDestroy(): void {
    this.pageSizeChange.complete();
  }

  ngOnInit(): void {
    this.initActiveTabFromQueryParams();
    this.initUserList();
    this.subscribeToSearchFilter();
    this.subscribeToWsUpdates();
    this.subscribeToRefreshBatchInList();
    this.subscribeToPageSizeChange();
  }

  get fulfillmentReadyBatches() {
    return this._fulfillmentReadyBatches.asReadonly();
  }

  get deliveryReadyBatches() {
    return this._deliveryReadyBatches.asReadonly();
  }

  get fulfillmentReadyTotalCount() {
    return this._fulfillmentReadyTotalCount.asReadonly();
  }

  get deliveryReadyTotalCount() {
    return this._deliveryReadyTotalCount.asReadonly();
  }

  get fulfillmentCountLoading() {
    return this._fulfillmentCountLoading.asReadonly();
  }

  get deliveryCountLoading() {
    return this._deliveryCountLoading.asReadonly();
  }

  handlePageSizeChange(newPageSize: number): void {
    this.pageSizeChange.next(newPageSize);
  }
  // ============================================
  // PUBLIC API FOR CHILD COMPONENTS
  // ============================================

  initPageSize(pageSize: number): void {
    const currentFulfillment = this._fulfillmentReadyBatches();
    const currentDelivery = this._deliveryReadyBatches();

    // Skip if page size hasn't changed and data is already loaded
    if (currentFulfillment.pageSize === pageSize && currentFulfillment.pageCache.size > 0) {
      return;
    }

    // Update page size for both states
    this._fulfillmentReadyBatches.set({
      ...currentFulfillment,
      pageSize,
      pageCache: new Map(),
      loadingPages: new Set(),
    });

    this._deliveryReadyBatches.set({
      ...currentDelivery,
      pageSize,
      pageCache: new Map(),
      loadingPages: new Set(),
    });

    // Fetch counts for both tabs (parallel)
    this.fetchCount(OrderBatchCategory.FULFILLMENT_READY).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.fetchCount(OrderBatchCategory.DELIVERY_READY).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    // Load first page for both tabs
    this.loadPageToCache(OrderBatchCategory.FULFILLMENT_READY, 1);
    this.loadPageToCache(OrderBatchCategory.DELIVERY_READY, 1);
  }

  loadPageToCache(category: OrderBatchCategory, page: number): void {
    const batchesSignal =
      category === OrderBatchCategory.FULFILLMENT_READY ? this._fulfillmentReadyBatches : this._deliveryReadyBatches;
    const state = batchesSignal();

    // Skip if already cached or loading
    if (state.pageCache.has(page) || state.loadingPages.has(page)) {
      return;
    }

    // Mark as loading
    const newLoadingPages = new Set(state.loadingPages);
    newLoadingPages.add(page);
    batchesSignal.set({ ...state, loadingPages: newLoadingPages });

    this.orderBatchService
      .getBatchesPage({
        category,
        skip: (page - 1) * state.pageSize,
        take: state.pageSize,
        virtualColumns: FulfillBatchListV2Component._BATCH_DTO_VIRTUAL_COLUMNS,
        ...state.filters,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ batches }) => {
          const currentState = batchesSignal();
          const newCache = new Map(currentState.pageCache);
          newCache.set(page, batches);
          const newLoading = new Set(currentState.loadingPages);
          newLoading.delete(page);

          batchesSignal.set({
            ...currentState,
            pageCache: newCache,
            loadingPages: newLoading,
          });

          this.setTemporaryCountIfNeeded(category, page, batches.length);
        },
        error: () => {
          const currentState = batchesSignal();
          const newLoading = new Set(currentState.loadingPages);
          newLoading.delete(page);
          batchesSignal.set({ ...currentState, loadingPages: newLoading });
          this.toastService.showToast({ message: 'Failed to load batches', mode: 'danger' });
        },
      });
  }

  preloadAdjacentPages(category: OrderBatchCategory, currentPage: number): void {
    const state =
      category === OrderBatchCategory.FULFILLMENT_READY
        ? this._fulfillmentReadyBatches()
        : this._deliveryReadyBatches();
    const totalCount =
      category === OrderBatchCategory.FULFILLMENT_READY
        ? this._fulfillmentReadyTotalCount()
        : this._deliveryReadyTotalCount();
    const totalPages = Math.ceil(totalCount / state.pageSize);

    if (currentPage > 1) {
      this.loadPageToCache(category, currentPage - 1);
    }
    if (currentPage < totalPages) {
      this.loadPageToCache(category, currentPage + 1);
    }
  }

  onLoadPage(category: OrderBatchCategory, event: { page: number }): void {
    this.loadPageToCache(category, event.page);
  }

  scanLabel() {
    this.fulfillService.togglePanel(true);
    this.fulfillService.setPanelState(PanelState.SCAN);
  }

  setActiveTab(tab: OrderBatchCategory): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
    });
  }

  shippingManifests() {
    const fulfillmentState = this._fulfillmentReadyBatches();
    const deliveryState = this._deliveryReadyBatches();

    const allBatches: OrderBatchDTO[] = [];
    fulfillmentState.pageCache.forEach((batches) => allBatches.push(...batches));
    deliveryState.pageCache.forEach((batches) => allBatches.push(...batches));

    this.dialog.open(ShippingManifestsListComponent, {
      width: '974px',
      height: '855px',
      minHeight: '500px',
      maxWidth: '90vw',
      maxHeight: 'calc(100vh - 3.75rem)',
      panelClass: 'overflow-visible-class',
      disableClose: true,
      data: {
        shopMetadata: this.fulfillService.shopMetadata,
      },
    });
  }

  setFilters(category: OrderBatchCategory, filters: BatchFilters): void {
    const batchesSignal =
      category === OrderBatchCategory.FULFILLMENT_READY ? this._fulfillmentReadyBatches : this._deliveryReadyBatches;
    const countSignal =
      category === OrderBatchCategory.FULFILLMENT_READY
        ? this._fulfillmentReadyTotalCount
        : this._deliveryReadyTotalCount;
    const currentState = batchesSignal();

    // Reset count so the tab label doesn't show stale values while loading
    countSignal.set(0);

    batchesSignal.set({
      ...currentState,
      pageCache: new Map(),
      loadingPages: new Set(),
      filters,
    });

    // Fetch new count and first page
    this.fetchCount(category).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.loadPageToCache(category, 1);
  }

  // ============================================
  // BATCH REORDERING
  // ============================================

  onBatchReorder(
    category: OrderBatchCategory,
    event: { movedBatch: OrderBatchDTO; targetBatch: OrderBatchDTO; insertBefore: boolean },
  ): void {
    const { movedBatch, targetBatch, insertBefore } = event;
    const batchesSignal =
      category === OrderBatchCategory.FULFILLMENT_READY ? this._fulfillmentReadyBatches : this._deliveryReadyBatches;

    // Optimistically update the cache
    this.reorderBatchInCache(batchesSignal, movedBatch.id, targetBatch.id, insertBefore);

    this.orderBatchService
      .moveBatchPriority({
        category,
        batchId: movedBatch.id,
        ...(insertBefore ? { beforeBatchId: targetBatch.id } : { afterBatchId: targetBatch.id }),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toastService.showToast({ message: 'Failed to reorder batch', mode: 'danger' });
          // Revert the optimistic update by reordering back
          this.reorderBatchInCache(batchesSignal, movedBatch.id, targetBatch.id, !insertBefore);
          return of(null);
        }),
      )
      .subscribe();
  }

  private subscribeToRefreshBatchInList(): void {
    this.fulfillService.refreshBatchInList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ batchId }) => {
      this.updateBatchInCache(batchId);
    });
  }

  private initActiveTabFromQueryParams(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === OrderBatchCategory.FULFILLMENT_READY || tab === OrderBatchCategory.DELIVERY_READY) {
      this.activeTab = tab;
    }
  }

  private subscribeToSearchFilter(): void {
    this.fulfillService.onHasFilter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((searchQuery: string) => {
      this.applySearchFilter(searchQuery);
    });
  }

  private initUserList(): void {
    this.umService.users$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => {
      this.pdqUsersList = users;
    });
  }

  private subscribeToPageSizeChange(): void {
    this.pageSizeChange
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(200))
      .subscribe((newPageSize: number) => {
        this.initPageSize(newPageSize);
      });
  }

  private subscribeToWsUpdates(): void {
    // Main batch updates (batch data changed, deleted, new batch ready, etc.)
    this.wssService
      .fireRefreshBatches()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: BatchSocketResponse) => {
        this.handleBatchWsUpdate(data);
      });

    // Track fulfillment changes (order fulfilled within a batch)
    this.wssService
      .fireTrackOrdersFulfillChanges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: BatchSocketResponse) => {
        // In paginated DTO we don't have orders array, but if batch metadata is included, update it
        const batchId = data.batchId;
        if (batchId) {
          this.updateBatchInCache(batchId);
        }
      });

    this.wssService
      .fireBatchFulfillmentProcess()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: BatchSocketResponse) => {
        const batchId = data.bulkFulfillmentProcess?.batchId;
        if (batchId) {
          this.updateBatchInCache(batchId);
        }
      });
  }

  private handleBatchWsUpdate(data: BatchSocketResponse): void {
    if (data.orderBatch?.hidden) {
      return;
    }

    // Note: Active order and panel state updates are handled by FulfillService.subscribeToWebhooks()

    // Delete batch
    if (data.batchId && data.deleted) {
      this.deleteBatchFromCache(data.batchId);
      return;
    }

    // New batch finished running and is now ready for fulfillment
    if (this.isNewBatchReady(data)) {
      const isExtraWS = this.isBatchInCache(this._fulfillmentReadyBatches(), data.orderBatch.id);
      isExtraWS ? this.updateBatchInCache(data.orderBatch.id) : this.handleNewBatchReady();
      return;
    }

    // Update batch (if in cache)
    if (data.orderBatch?.id) {
      this.updateBatchInCache(data.orderBatch.id);
    }
  }

  /**
   * Checks if the WS event indicates a new batch is ready.
   * Mirrors the logic from FulfillService.prepareBatchIsDone
   */
  private isNewBatchReady(data: BatchSocketResponse): boolean {
    return !!(
      data.counters &&
      data.mode === 'done' &&
      data.orderBatch &&
      this.fulfillService.isUserLocationsIncludeBatch(data.orderBatch)
    );
  }

  private updateBatchInCache(batchId: number): void {
    const isInFulfillmentCache = this.isBatchInCache(this._fulfillmentReadyBatches(), batchId);
    const isInDeliveryCache = this.isBatchInCache(this._deliveryReadyBatches(), batchId);
    if (!isInFulfillmentCache && !isInDeliveryCache) {
      return;
    }

    const category = isInFulfillmentCache ? OrderBatchCategory.FULFILLMENT_READY : OrderBatchCategory.DELIVERY_READY;
    const batchesSignal = isInFulfillmentCache ? this._fulfillmentReadyBatches : this._deliveryReadyBatches;
    const countSignal = isInFulfillmentCache ? this._fulfillmentReadyTotalCount : this._deliveryReadyTotalCount;
    this.refreshOrRemoveBatch(batchId, category, batchesSignal, countSignal);
  }

  private buildTabLabel(title: string, count: number, loading: boolean): string {
    if (loading && count === 0) {
      return title;
    }
    const suffix = loading ? '+' : '';
    return `${title} (${count}${suffix})`;
  }

  private refreshOrRemoveBatch(
    batchId: number,
    category: OrderBatchCategory,
    batchesSignal: WritableSignal<BatchesState>,
    countSignal: WritableSignal<number>,
  ): void {
    const state = batchesSignal();
    const table = category === OrderBatchCategory.FULFILLMENT_READY ? this.fulfillmentTable : this.deliveryTable;

    // Using pagination route with batchId filter validates that batch still matches current filters
    this.orderBatchService
      .getBatchesPage({
        category,
        skip: 0,
        take: 1,
        virtualColumns: FulfillBatchListV2Component._BATCH_DTO_VIRTUAL_COLUMNS,
        ...state.filters,
        batchId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ batches }) => {
          if (batches.length > 0) {
            // Batch still matches filters - update in cache
            this.replaceBatchInSignal(batchesSignal, batchId, batches[0]);
          } else {
            // Batch no longer matches filters or category changed - remove using fetch-then-invalidate
            this.handleBatchRemoval(batchId, category, batchesSignal, countSignal, table?.getCurrentPage() ?? 1);

            // If removed from fulfillment ready, check if it moved to delivery ready
            if (category === OrderBatchCategory.FULFILLMENT_READY) {
              this.checkWhetherBatchMovedToDeliveryReady(batchId);
            }
          }
        },
        error: () => {
          // Silently ignore - batch may have been deleted or moved
        },
      });
  }

  private isBatchInCache(state: BatchesState, batchId: number): boolean {
    const allBatches = [...state.pageCache.values()].flat();
    return allBatches.some((b) => b.id === batchId);
  }

  /**
   * Finds the page entry containing a batch, or null if not found.
   */
  private findBatchPage(pageCache: Map<number, OrderBatchDTO[]>, batchId: number): [number, OrderBatchDTO[]] | null {
    return [...pageCache.entries()].find(([_, batches]) => batches.some((b) => b.id === batchId)) ?? null;
  }

  private replaceBatchInSignal(
    batchesSignal: WritableSignal<BatchesState>,
    batchId: number,
    freshBatch: OrderBatchDTO,
  ): void {
    // Use signal.update to prevent race conditions with concurrent WS updates
    batchesSignal.update((state) => {
      const newCache = new Map<number, OrderBatchDTO[]>();

      for (const [page, batches] of state.pageCache.entries()) {
        const updatedBatches = batches.map((b) => (b.id === batchId ? freshBatch : b));
        newCache.set(page, updatedBatches);
      }

      return { ...state, pageCache: newCache };
    });
  }

  private deleteBatchFromCache(batchId: number): void {
    const isInFulfillmentCache = this.isBatchInCache(this._fulfillmentReadyBatches(), batchId);
    const isInDeliveryCache = this.isBatchInCache(this._deliveryReadyBatches(), batchId);

    if (!isInFulfillmentCache && !isInDeliveryCache) {
      return;
    }
    const category = isInFulfillmentCache ? OrderBatchCategory.FULFILLMENT_READY : OrderBatchCategory.DELIVERY_READY;
    const currentBatchesSignal = isInFulfillmentCache ? this._fulfillmentReadyBatches : this._deliveryReadyBatches;
    const currentCountSignal = isInFulfillmentCache ? this._fulfillmentReadyTotalCount : this._deliveryReadyTotalCount;
    const currentPage = isInFulfillmentCache
      ? (this.fulfillmentTable?.getCurrentPage() ?? 1)
      : (this.deliveryTable?.getCurrentPage() ?? 1);
    this.handleBatchRemoval(batchId, category, currentBatchesSignal, currentCountSignal, currentPage);
  }

  /**
   * Handles batch removal by determining affected page and triggering cache update.
   */
  private handleBatchRemoval(
    batchId: number,
    category: OrderBatchCategory,
    currentBatches: WritableSignal<BatchesState>,
    currentTotalCount: WritableSignal<number>,
    currentPage: number,
  ): void {
    const state = currentBatches();
    const pageEntry = this.findBatchPage(state.pageCache, batchId);

    if (!pageEntry) {
      return;
    }

    const table = category === OrderBatchCategory.FULFILLMENT_READY ? this.fulfillmentTable : this.deliveryTable;
    table?.unselectBatch(batchId);

    const [affectedPage] = pageEntry;
    this.handlePagesRequireUpdate(category, currentBatches, affectedPage, currentPage);
    currentTotalCount.update((c) => Math.max(0, c - 1));
  }

  /**
   * Checks if a batch that was removed from fulfillment ready has moved to delivery ready.
   * If found in delivery ready, updates the cache accordingly.
   */
  private checkWhetherBatchMovedToDeliveryReady(batchId: number): void {
    const state = this._deliveryReadyBatches();

    this.orderBatchService
      .getBatchesPage({
        category: OrderBatchCategory.DELIVERY_READY,
        skip: 0,
        take: 1,
        ...state.filters,
        batchId,
        virtualColumns: FulfillBatchListV2Component._BATCH_DTO_VIRTUAL_COLUMNS,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ batches }) => {
          if (batches.length > 0) {
            // Batch moved to delivery ready - increment count
            this._deliveryReadyTotalCount.update((c) => c + 1);

            const deliveryState = this._deliveryReadyBatches();
            // Only update cache if page 1 is already loaded
            if (!deliveryState.pageCache.has(1)) {
              return;
            }

            const currentPage = this.deliveryTable?.getCurrentPage() ?? 1;
            this.handlePagesRequireUpdate(
              OrderBatchCategory.DELIVERY_READY,
              this._deliveryReadyBatches,
              1,
              currentPage,
            );
          }
        },
      });
  }

  /**
   * Handles page cache updates when data shifts (batch added or removed).
   * Fetches pages near current viewport, invalidates pages far from viewport.
   * @param affectedPage - First page affected by the change (pages >= this need refresh)
   * @param currentPage - Current viewport page (used to determine safe zone)
   */
  private handlePagesRequireUpdate(
    category: OrderBatchCategory,
    batchesSignal: WritableSignal<BatchesState>,
    affectedPage: number,
    currentPage: number,
  ): void {
    const state = batchesSignal();

    // Get all cached pages that need to be refreshed (pages >= affectedPage)
    const pagesToRefresh = [...state.pageCache.keys()].filter((p) => p >= affectedPage).sort((a, b) => a - b);

    if (pagesToRefresh.length === 0) {
      return;
    }

    // Define safe zone: pages within 2 pages of current viewport
    const safeZoneBuffer = 2;
    const safeZoneStart = currentPage - safeZoneBuffer;
    const safeZoneEnd = currentPage + safeZoneBuffer;

    // Split pages into: pages to fetch (near viewport) and pages to invalidate (far from viewport)
    const pagesToFetch = pagesToRefresh.filter((p) => p >= safeZoneStart && p <= safeZoneEnd);
    const pagesToInvalidate = pagesToRefresh.filter((p) => p < safeZoneStart || p > safeZoneEnd);

    // Fetch pages near viewport, then invalidate far-away pages
    if (pagesToFetch.length > 0) {
      this.loadMultiplePages(category, batchesSignal, pagesToFetch, state.pageSize, state.filters)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.invalidatePages(batchesSignal, pagesToInvalidate);
        });
    } else {
      this.invalidatePages(batchesSignal, pagesToInvalidate);
    }
  }

  private loadMultiplePages(
    category: OrderBatchCategory,
    batchesSignal: WritableSignal<BatchesState>,
    pages: number[],
    pageSize: number,
    filters: BatchFilters,
  ): Observable<void> {
    const fetchObservables = pages.map((page) =>
      this.orderBatchService
        .getBatchesPage({
          category,
          skip: (page - 1) * pageSize,
          take: pageSize,
          ...filters,
          virtualColumns: FulfillBatchListV2Component._BATCH_DTO_VIRTUAL_COLUMNS,
        })
        .pipe(
          map(({ batches }) => ({ page, batches })),
          catchError(() => of({ page, batches: null as OrderBatchDTO[] | null })),
        ),
    );

    return forkJoin(fetchObservables).pipe(
      tap((results) => {
        batchesSignal.update((prevState) => {
          const newCache = new Map(prevState.pageCache);

          results.forEach(({ page, batches }) => {
            batches ? newCache.set(page, batches) : newCache.delete(page);
          });

          return { ...prevState, pageCache: newCache };
        });
      }),
      map(() => void 0),
    );
  }

  private invalidatePages(batchesSignal: WritableSignal<BatchesState>, pages: number[]): void {
    if (pages.length === 0) {
      return;
    }

    batchesSignal.update((prevState) => {
      const newCache = new Map(prevState.pageCache);
      pages.forEach((p) => newCache.delete(p));
      return { ...prevState, pageCache: newCache };
    });
  }

  /**
   * Sets a temporary count from page 1 data if the count request hasn't completed yet.
   * This allows showing the first page immediately while waiting for the count response.
   * Also stops loading indicator if we received fewer batches than page size (single page).
   */
  private setTemporaryCountIfNeeded(category: OrderBatchCategory, page: number, batchesLength: number): void {
    if (page !== 1) {
      return;
    }

    const batchesSignal =
      category === OrderBatchCategory.FULFILLMENT_READY ? this._fulfillmentReadyBatches : this._deliveryReadyBatches;
    const countSignal =
      category === OrderBatchCategory.FULFILLMENT_READY
        ? this._fulfillmentReadyTotalCount
        : this._deliveryReadyTotalCount;
    const loadingSignal =
      category === OrderBatchCategory.FULFILLMENT_READY ? this._fulfillmentCountLoading : this._deliveryCountLoading;
    const pageSize = batchesSignal().pageSize;

    // If we received fewer batches than page size, we know this is the only page
    // No need to wait for count - set final count and stop loading
    if (batchesLength < pageSize) {
      countSignal.set(batchesLength);
      loadingSignal.set(false);
      return;
    }

    // Otherwise, set temporary count if count hasn't arrived yet
    if (batchesLength > 0 && countSignal() === 0) {
      countSignal.set(batchesLength);
    }
  }

  /**
   * Handles a new batch becoming ready for fulfillment.
   * New batches always appear on page 1, so all cached pages are affected.
   */
  private handleNewBatchReady(): void {
    this.fetchCount(OrderBatchCategory.FULFILLMENT_READY).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    const state = this._fulfillmentReadyBatches();

    // If page 1 is not cached, nothing to update - data will be fetched on demand
    if (!state.pageCache.has(1)) {
      return;
    }

    const currentPage = this.fulfillmentTable?.getCurrentPage() ?? 1;
    // New batch goes to page 1, so all pages from 1 onwards are affected
    this.handlePagesRequireUpdate(OrderBatchCategory.FULFILLMENT_READY, this._fulfillmentReadyBatches, 1, currentPage);
  }

  private applySearchFilter(searchQuery: string): void {
    const prevFulfillment = this._fulfillmentReadyBatches();
    const prevDelivery = this._deliveryReadyBatches();

    // Reset counts so the tab label doesn't show stale values while loading
    this._fulfillmentReadyTotalCount.set(0);
    this._deliveryReadyTotalCount.set(0);

    // Clear caches and set new filters
    this._fulfillmentReadyBatches.set({
      ...prevFulfillment,
      pageCache: new Map(),
      loadingPages: new Set(),
      filters: { ...prevFulfillment.filters, searchQuery },
    });

    this._deliveryReadyBatches.set({
      ...prevDelivery,
      pageCache: new Map(),
      loadingPages: new Set(),
      filters: { ...prevDelivery.filters, searchQuery },
    });

    // Fetch counts and pages in parallel
    forkJoin({
      fulfillmentCount: this.fetchCount(OrderBatchCategory.FULFILLMENT_READY),
      deliveryCount: this.fetchCount(OrderBatchCategory.DELIVERY_READY),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ fulfillmentCount, deliveryCount }) => {
        // Auto-switch tab based on results
        if (fulfillmentCount === 0 && deliveryCount > 0) {
          this.setActiveTab(OrderBatchCategory.DELIVERY_READY);
        } else if (deliveryCount === 0 && fulfillmentCount > 0) {
          this.setActiveTab(OrderBatchCategory.FULFILLMENT_READY);
        }
      });

    // Reset tables to first page
    this.fulfillmentTable?.resetToFirstPage();
    this.deliveryTable?.resetToFirstPage();

    // Load first page for both tabs
    this.loadPageToCache(OrderBatchCategory.FULFILLMENT_READY, 1);
    this.loadPageToCache(OrderBatchCategory.DELIVERY_READY, 1);
  }

  private fetchCount(category: OrderBatchCategory) {
    const state =
      category === OrderBatchCategory.FULFILLMENT_READY
        ? this._fulfillmentReadyBatches()
        : this._deliveryReadyBatches();
    const loadingSignal =
      category === OrderBatchCategory.FULFILLMENT_READY ? this._fulfillmentCountLoading : this._deliveryCountLoading;

    loadingSignal.set(true);

    return this.orderBatchService.getBatchesCount({ category, ...state.filters }).pipe(
      tap(({ count }) => {
        if (category === OrderBatchCategory.FULFILLMENT_READY) {
          this._fulfillmentReadyTotalCount.set(count);
        } else {
          this._deliveryReadyTotalCount.set(count);
        }
        loadingSignal.set(false);
      }),
      map(({ count }) => count),
      catchError(() => {
        loadingSignal.set(false);
        this.toastService.showToast({ message: 'Failed to load batch count', mode: 'danger' });
        return of(0);
      }),
    );
  }

  private reorderBatchInCache(
    batchesSignal: WritableSignal<BatchesState>,
    movedBatchId: number,
    targetBatchId: number,
    insertBefore: boolean,
  ): void {
    batchesSignal.update((state) => {
      // Flatten all cached batches into a single array preserving order
      const allPages = [...state.pageCache.keys()].sort((a, b) => a - b);
      const allBatches: OrderBatchDTO[] = [];
      allPages.forEach((page) => {
        const batches = state.pageCache.get(page);
        if (batches) {
          allBatches.push(...batches);
        }
      });

      // Find and remove the moved batch
      const movedIndex = allBatches.findIndex((b) => b.id === movedBatchId);
      if (movedIndex === -1) return state;

      const [movedBatch] = allBatches.splice(movedIndex, 1);

      // Find the target position and insert
      const targetIndex = allBatches.findIndex((b) => b.id === targetBatchId);
      if (targetIndex === -1) return state;

      const insertIndex = insertBefore ? targetIndex : targetIndex + 1;
      allBatches.splice(insertIndex, 0, movedBatch);

      // Rebuild the page cache with the new order
      const newCache = new Map<number, OrderBatchDTO[]>();
      let batchIndex = 0;
      allPages.forEach((page) => {
        const pageSize = state.pageCache.get(page)?.length ?? state.pageSize;
        const pageBatches = allBatches.slice(batchIndex, batchIndex + pageSize);
        if (pageBatches.length > 0) {
          newCache.set(page, pageBatches);
        }
        batchIndex += pageSize;
      });

      return { ...state, pageCache: newCache };
    });
  }
}
